import * as React from 'react';
import Box, { BoxProps } from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import { isImpersonating } from 'api';
import { useUserIsSetupCorrectly } from '@hooks/useUserIsSetupCorrectly';

function Root(props: BoxProps) {
  return (
    <Box
      {...props}
      sx={[
        {
          bgcolor: 'background.appBody',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            // sm: 'minmax(64px, 200px) minmax(450px, 1fr)',
            // md: 'minmax(160px, 300px) minmax(300px, 500px) minmax(500px, 1fr)',
            // md: 'minmax(70px, 70px) minmax(300px, 500px) minmax(500px, 1fr)', // this layout is useful when we want to have a secondary sidebar
            md: '70px 1fr', // this layout is useful when we want to have a secondary sidebar
          },
          gridTemplateRows: '64px 1fr',
          minHeight: '100vh',
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function Header(props: BoxProps) {
  return (
    <Box
      component="header"
      className="Header"
      {...props}
      sx={[
        {
          p: 2,
          gap: 2,
          bgcolor: 'background.surface',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gridColumn: '1 / -1',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          height: 'var(--Header-height)',
          ...(isImpersonating ? {

            backgroundImage: 'linear-gradient(180deg, #BD2025 0.5rem, transparent 0.5rem)',
            backgroundRepeat: 'repeat-x',
            // backgroundImage: 'linear-gradient(45deg, #faebec 16.67%, #fafafa 16.67%, #fafafa 50%, #faebec 50%, #faebec 66.67%, #fafafa 66.67%, #fafafa 100%)',
            // backgroundSize: '42.43px 42.43px',
          } : {}),
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function SideNav(props: BoxProps) {
  const isSetupCorrectly = useUserIsSetupCorrectly();

  // this is hacky for responsive but whatever
  const headerHeight = isSetupCorrectly ? '64px' : '109px';

  return (
    <Box
      component="nav"
      className="Navigation"
      {...props}
      sx={[
        {

            /*
            use a variation of this to make the bar appear "floating"
            background: #ccc;
            border-radius: 8px;
            margin: 8px;
            padding: 9px;
            */

        //   p: 2,
          bgcolor: 'background.surface',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: {
            xs: 'none',
            md: 'initial',
          },
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
        <Box
            sx={{
                height: `calc(100vh - ${headerHeight})`,
                position: 'fixed',
                top: headerHeight,
                width: '70px',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                p: 2,
            }}
        >
            {props.children}
        </Box>
    </Box>
  );
}

function SidePane(props: BoxProps) {
  return (
    <Box
      className="Inbox"
      {...props}
      sx={[
        {
          bgcolor: 'background.surface',
          borderRight: '1px solid',
          borderColor: 'divider',
          display: {
            xs: 'none',
            md: 'initial',
          },
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    />
  );
}

function Main(props: BoxProps) {
  return (
    <Box
      component="main"
      className="Main"
      {...props}
      sx={[
        {
          // p: 2,
          // todo: figure out how messy this is
          display: 'flex',
          flexDirection: 'column',
          // justifyContent: 'center',
          // position: 'relative',
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx])
      ]}
    />
  );
}

function SideDrawer(
  props: BoxProps & { onClose: React.MouseEventHandler<HTMLDivElement> },
) {
  const { onClose, ...other } = props;
  return (
    <Box
      {...other}
      sx={[
        { position: 'fixed', zIndex: 1200, width: '100%', height: '100%' },
        ...(Array.isArray(other.sx) ? other.sx : [other.sx]),
      ]}
    >
      <Box
        role="button"
        onClick={onClose}
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: (theme) =>
            `rgba(${theme.vars.palette.neutral.darkChannel} / 0.8)`,
        }}
      />
      <Sheet
        sx={{
          minWidth: 256,
          width: 'max-content',
          height: '100%',
          p: 2,
          boxShadow: 'lg',
          bgcolor: 'background.surface',
        }}
      >
        {other.children}
      </Sheet>
    </Box>
  );
}

export default {
  Root,
  Header,
  SideNav,
  SidePane,
  SideDrawer,
  Main,
};
